import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import {useNavigate} from "react-router";
import {useNews} from "../firebase/useNews";
import {NewsCard} from "../news/NewsCard";

export const NewsSection = () => {
    const navigate = useNavigate();
    const news = useNews({
        orderBy: "published",
        order: "desc",
    });

    // Show up to 3 most recent published articles on the landing page
    const publishedArticles = (news || []).filter((art) => art.status !== "draft");
    const featuredArticles = publishedArticles.slice(0, 3);

    return (
        <Box
            component="section"
            id="aktuelt"
            aria-labelledby="aktuelt-title"
            sx={{
                py: {xs: 4, md: 6},
                width: "100%",
                maxWidth: "100%",
            }}
        >
            {/* Section Header */}
            <Box sx={{textAlign: "center", mb: 4, maxWidth: 760, mx: "auto"}}>
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "rgba(40, 82, 56, 0.08)",
                        color: "primary.main",
                        px: 2,
                        py: 0.5,
                        borderRadius: 3,
                        mb: 1.5,
                    }}
                >
                    <NewspaperIcon sx={{fontSize: 18}} />
                    <Typography variant="caption" sx={{fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase"}}>
                        Siste nytt
                    </Typography>
                </Box>
                <Typography
                    id="aktuelt-title"
                    variant="h4"
                    component="h2"
                    sx={{
                        fontWeight: 800,
                        fontSize: {xs: "1.6rem", sm: "2rem", md: "2.25rem"},
                        color: "primary.main",
                        mb: 1.5,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Aktuelt og kunngjøringer
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{fontSize: {xs: "0.975rem", sm: "1.05rem"}, lineHeight: 1.7}}
                >
                    Her finner du nyheter om kursstarter, faglige oppdateringer og arrangementer innen bekymringsmestring.
                </Typography>
            </Box>

            {/* Articles Grid or Placeholder */}
            {featuredArticles.length > 0 ? (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            sm: featuredArticles.length === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
                            md: featuredArticles.length <= 2 ? `repeat(${featuredArticles.length}, minmax(0, 1fr))` : "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 3,
                        mb: 4,
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                    }}
                >
                    {featuredArticles.map((article) => (
                        <NewsCard key={article.id} article={article} />
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "background.paper",
                        borderRadius: 3,
                        border: "1px dashed rgba(40, 82, 56, 0.2)",
                        mb: 4,
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        Ingen nyhetsartikler er publisert ennå. Følg med for kommende kursdatoer og nyheter.
                    </Typography>
                </Box>
            )}

            {/* CTA to all news */}
            <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/news")}
                    sx={{
                        fontWeight: 700,
                        px: {xs: 2, sm: 3.5},
                        py: 1.25,
                        borderRadius: 2.5,
                        maxWidth: "100%",
                        whiteSpace: {xs: "normal", sm: "nowrap"},
                        textTransform: "none",
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: 2,
                        },
                    }}
                >
                    Se alle nyheter og oppdateringer
                </Button>
            </Box>
        </Box>
    );
};
